/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useState } from 'react';

import { cssMap as cssMapCompiled } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap, cx, jsx } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import LockIcon from '@atlaskit/icon/core/migration/lock-locked--lock';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import messages from './messages';

const styles = cssMap({
	copyButton: {
		width: '32px',
		height: '32px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		'&:hover': {
			backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
		},
		borderRadius: token('border.radius.100', '3px'),
	},

	fullSizeText: { maxHeight: '100%', overflowY: 'auto' },

	instructionsContainer: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.input'),
		borderRadius: token('border.radius.100'),
		whiteSpace: 'pre-line',
		position: 'relative',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		flex: 1,
	},

	smallText: {
		maxHeight: '400px',
		overflowY: 'scroll',
	},

	hidden: {
		opacity: 0,
	},

	heading: {
		display: 'flex',
	},

	forgeAgentWrapper: {
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('border.radius.100'),
	},
});

const stylesCompiled = cssMapCompiled({
	copyContainer: {
		position: 'absolute',
		top: '10px',
		right: '10px',
	},
});

type AgentInstructionsProps = {
	promptTemplate?: string | null;
	onCopy: (promptTemplate: string) => void;
	isForgeAgent: boolean;
	fullHeightText?: boolean;
};

export const AgentInstructions = ({
	promptTemplate,
	onCopy,
	isForgeAgent,
	fullHeightText = true,
}: AgentInstructionsProps) => {
	const { formatMessage } = useIntl();
	const [instructionsHovered, setInstructionsHovered] = useState(false);

	const getInstructionsRender = () => {
		if (isForgeAgent) {
			return (
				<Inline xcss={styles.forgeAgentWrapper} space="space.100">
					<LockIcon color="currentColor" spacing="spacious" label="" />
					<Stack space="space.100" alignBlock="start">
						<Text weight="bold">{formatMessage(messages.privateInstructionsTitle)}</Text>
						<Text size="small">{formatMessage(messages.privateInstructionsDescription)}</Text>
					</Stack>
				</Inline>
			);
		}

		return (
			<Box
				xcss={cx(
					styles.instructionsContainer,
					fullHeightText ? styles.fullSizeText : styles.smallText,
				)}
				onMouseEnter={() => {
					setInstructionsHovered(true);
				}}
				onMouseLeave={() => {
					setInstructionsHovered(false);
				}}
			>
				{promptTemplate ? (
					<>
						<Text>{promptTemplate}</Text>
						<div css={[stylesCompiled.copyContainer, !instructionsHovered && styles.hidden]}>
							<Tooltip content={formatMessage(messages.copyInstructionsTooltip)} position="top">
								<Pressable
									aria-label={formatMessage(messages.copyInstructionsTooltip)}
									xcss={styles.copyButton}
									onBlur={() => setInstructionsHovered(false)}
									onFocus={() => setInstructionsHovered(true)}
									onClick={() => onCopy(promptTemplate)}
								>
									<CopyIcon color="var(--ds-icon)" label="" />
								</Pressable>
							</Tooltip>
						</div>
					</>
				) : (
					<Text as="em" color="color.text.subtle">
						{formatMessage(messages.noInstructionPlaceholder)}
					</Text>
				)}
			</Box>
		);
	};

	return (
		<Stack space="space.100" grow="fill" alignBlock="start">
			<>
				<Box as="h2" xcss={styles.heading}>
					<Text size="large" color="color.text.subtle" weight="semibold">
						{formatMessage(messages.instructionsLabel)}
					</Text>
				</Box>
				{getInstructionsRender()}
			</>
		</Stack>
	);
};
